export function formatFriendlyOpeningDatePhrase(isoString: string): string {
  const target = new Date(isoString);
  const now = new Date();

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const opening = new Date(target.getFullYear(), target.getMonth(), target.getDate());

  const millisPerDay = 24 * 60 * 60 * 1000;
  const diffDays = Math.round((opening.getTime() - today.getTime()) / millisPerDay);

  const timeString = target
    .toLocaleTimeString('en-GB', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    .toLowerCase();

  if (diffDays === 0) {
    return `Opens at ${timeString}`;
  }

  if (diffDays === 1) {
    return `Opens tomorrow at ${timeString}`;
  }

  if (diffDays > 1 && diffDays <= 6) {
    const weekday = target.toLocaleDateString('en-GB', { weekday: 'long' });
    return `Opens ${weekday} at ${timeString}`;
  }

  const longDate = target.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  });

  return `Opens ${longDate} at ${timeString}`;
}

export function formatFriendlyClosingDatePhrase(isoString: string): string {
  const target = new Date(isoString);
  const timePhrase = target
    .toLocaleTimeString('en-GB', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    .toLowerCase();

  return `Closes at ${timePhrase}`;
}

export function formatLongDate(isoString: string): string {
  const target = new Date(isoString);
  return target.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

// ENG-21800 Appointments send ISO throughout; every label below is derived here
// rather than by the API, per Plan - Appointments integration.

/** Slot button label — '9:20am'. No space before am/pm, as in `formatDateTime`. */
export function formatSlotTime(isoString: string): string {
  return new Date(isoString)
    .toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true })
    .replace(/\s/g, '')
    .toLowerCase();
}

/** Day group heading — 'Monday 11 August'. */
export function formatDayHeading(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

/** Accessible name for a slot button — the day travels with the time. */
export function formatSlotAccessibleName(isoDate: string, isoStartsAt: string): string {
  return `${formatDayHeading(isoDate)}, ${formatSlotTime(isoStartsAt)}`;
}

export function formatHoursToReadableTime(hours: number): string {
  if (hours < 0) {
    return '0 hours';
  }

  const weeks = Math.floor(hours / 168);
  const remainingAfterWeeks = hours % 168;
  const days = Math.floor(remainingAfterWeeks / 24);
  const remainingHours = Math.floor(remainingAfterWeeks % 24);

  const parts: string[] = [];

  if (weeks > 0) {
    parts.push(`${weeks} ${weeks === 1 ? 'week' : 'weeks'}`);
  }

  if (days > 0) {
    parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
  }

  if (remainingHours > 0 || parts.length === 0) {
    parts.push(`${remainingHours} ${remainingHours === 1 ? 'hour' : 'hours'}`);
  }

  return parts.join(', ');
}

/** "10 August 2026 at 9:15am" — shared format for ISO values from the API. */
export function formatDateTime(isoString: string): string {
  const target = new Date(isoString);

  if (Number.isNaN(target.getTime())) {
    return '';
  }

  const time = target
    .toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true })
    .replace(/\s/g, '')
    .toLowerCase();

  return `${formatLongDate(isoString)} at ${time}`;
}
